import { Component } from '@theme/component';
import { debounce, isClickedOutside } from '@theme/utilities';

/**
 * @typedef {Object} AxPublicationsRefs
 * @property {HTMLInputElement} [searchInput] - Text search field (optional, gated by a section setting)
 * @property {HTMLButtonElement} [searchClear] - Button that clears the search field
 * @property {HTMLElement[]} [cards] - Publication cards, each carrying data-* facet attributes
 * @property {HTMLElement} [status] - aria-live region announcing the visible-card count
 * @property {HTMLElement} [emptyState] - "No results" message
 * @property {HTMLElement} [activeChips] - Container for removable active-filter chips
 * @property {HTMLButtonElement} [clearButton] - "Clear all" reset button
 */

/**
 * Library of publications.
 *
 * Client-side faceted filter + text search over server-rendered cards. Facet option lists are derived
 * from the cards' own data-* attributes (single source of truth), so the markup and the filter can never
 * drift apart. A card is visible when it matches the search query AND, for every dimension with at least
 * one selected value, it carries one of those values (OR within a dimension, AND across dimensions).
 *
 * Without JS every card stays visible (progressive enhancement).
 *
 * @extends {Component<AxPublicationsRefs>}
 */
class AxPublications extends Component {
  /** @type {string[]} */
  requiredRefs = [];

  #selectors = {
    facet: '.ax-publications__facet',
    facetOptions: '.ax-publications__facet-options',
    facetCount: '.ax-publications__facet-count',
    optionInput: '.ax-publications__option input',
    chipRemove: '[data-chip-remove]',
  };

  #classes = {
    facetActive: 'ax-publications__facet--active',
    option: 'ax-publications__option',
    chip: 'ax-publications__chip',
    chipRemove: 'ax-publications__chip-remove',
  };

  /** Ordered facet dimensions (match the section's data-dimension attributes). @type {string[]} */
  #dimensions = ['area', 'tags', 'biomarkers', 'year', 'type'];

  /** Dimensions whose card attribute holds several pipe-joined values. @type {Set<string>} */
  #multiValue = new Set(['tags', 'biomarkers']);

  /** dimension → Set of currently selected values. @type {Map<string, Set<string>>} */
  #selected = new Map();

  /** dimension → cached facet DOM. @type {Map<string, {details: HTMLDetailsElement, options: HTMLElement, count: HTMLElement | null}>} */
  #facets = new Map();

  /** Parsed card models. @type {Array<{el: HTMLElement, values: Record<string, string[]>, search: string}>} */
  #cards = [];

  /** Lowercased search query. @type {string} */
  #query = '';

  /** @type {(event: Event) => void} */
  #onSearch = debounce((event) => {
    const target = event.target;
    this.#query = target instanceof HTMLInputElement ? target.value.trim().toLowerCase() : '';
    this.#applyFilters();
  }, 200);

  /** Toggle the clear button immediately as the user types (not debounced). @type {() => void} */
  #toggleSearchClear = () => {
    const { searchInput, searchClear } = this.refs;
    if (searchInput && searchClear) searchClear.hidden = searchInput.value.length === 0;
  };

  /** Clear the search field and re-filter. @type {() => void} */
  #onSearchClear = () => {
    const input = this.refs.searchInput;
    if (!input) return;

    input.value = '';
    this.#query = '';
    if (this.refs.searchClear) this.refs.searchClear.hidden = true;
    this.#applyFilters();
    input.focus();
  };

  /** @type {(event: Event) => void} */
  #onFacetChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const set = this.#selected.get(input.dataset.dimension ?? '');
    if (!set) return;

    if (input.checked) set.add(input.value);
    else set.delete(input.value);

    this.#applyFilters();
  };

  /** @type {(event: Event) => void} */
  #onChipClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest(this.#selectors.chipRemove) : null;
    if (!(button instanceof HTMLElement)) return;

    this.#removeValue(button.dataset.dimension ?? '', button.dataset.value ?? '');
  };

  /**
   * Close any open facet dropdown when the click lands outside it. Clicking a facet's own summary or
   * options keeps it open; clicking another facet's summary closes this one (single-open behavior).
   * @type {(event: MouseEvent) => void}
   */
  #onDocumentClick = (event) => {
    for (const facet of this.querySelectorAll(`${this.#selectors.facet}[open]`)) {
      if (facet instanceof HTMLDetailsElement && isClickedOutside(event, facet)) facet.open = false;
    }
  };

  /**
   * Close open facet dropdowns on Escape and return focus to their summary.
   * @type {(event: KeyboardEvent) => void}
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;

    for (const facet of this.querySelectorAll(`${this.#selectors.facet}[open]`)) {
      if (!(facet instanceof HTMLDetailsElement)) continue;
      const summary = facet.querySelector('summary');
      facet.open = false;
      if (summary instanceof HTMLElement) summary.focus();
    }
  };

  connectedCallback() {
    super.connectedCallback();

    this.#cacheCards();
    this.#cacheFacets();
    this.#buildOptions();
    this.#attachListeners();
    this.#applyFilters();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeListeners();
  }

  /**
   * Reset every filter and the search query. Bound declaratively via on:click="/handleClearAll".
   */
  handleClearAll() {
    this.#query = '';
    if (this.refs.searchInput) this.refs.searchInput.value = '';

    for (const set of this.#selected.values()) set.clear();

    for (const input of this.querySelectorAll(this.#selectors.optionInput)) {
      if (input instanceof HTMLInputElement) input.checked = false;
    }

    this.#applyFilters();
  }

  /**
   * Parse each card's data-* attributes into a filter model once.
   */
  #cacheCards() {
    const cards = this.refs.cards ?? [];

    this.#cards = cards.map((el) => ({
      el,
      values: {
        area: this.#splitAttr(el.dataset.area, 'area'),
        tags: this.#splitAttr(el.dataset.tags, 'tags'),
        biomarkers: this.#splitAttr(el.dataset.biomarkers, 'biomarkers'),
        year: this.#splitAttr(el.dataset.year, 'year'),
        type: this.#splitAttr(el.dataset.type, 'type'),
      },
      search: (el.dataset.search ?? '').toLowerCase(),
    }));
  }

  /**
   * @param {string | undefined} value
   * @param {string} dimension
   * @returns {string[]}
   */
  #splitAttr(value, dimension) {
    if (!value) return [];

    const parts = this.#multiValue.has(dimension) ? value.split('|') : [value];
    return parts.map((part) => part.trim()).filter(Boolean);
  }

  /**
   * Cache the facet <details> elements and seed an empty selection set per dimension.
   */
  #cacheFacets() {
    for (const dimension of this.#dimensions) this.#selected.set(dimension, new Set());

    for (const details of this.querySelectorAll(this.#selectors.facet)) {
      if (!(details instanceof HTMLDetailsElement)) continue;

      const dimension = details.dataset.dimension;
      const options = details.querySelector(this.#selectors.facetOptions);
      if (!dimension || !(options instanceof HTMLElement)) continue;

      const count = details.querySelector(this.#selectors.facetCount);
      this.#facets.set(dimension, {
        details,
        options,
        count: count instanceof HTMLElement ? count : null,
      });
    }
  }

  /**
   * Build the checkbox option list of each facet from the distinct values found across all cards.
   * A facet with no values is hidden.
   */
  #buildOptions() {
    for (const [dimension, facet] of this.#facets) {
      const values = this.#uniqueValues(dimension);

      if (!values.length) {
        facet.details.hidden = true;
        continue;
      }

      const fragment = document.createDocumentFragment();

      for (const value of values) {
        const label = document.createElement('label');
        label.className = this.#classes.option;

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.value = value;
        input.dataset.dimension = dimension;

        const text = document.createElement('span');
        text.textContent = value;

        label.append(input, text);
        fragment.append(label);
      }

      facet.options.replaceChildren(fragment);
    }
  }

  /**
   * @param {string} dimension
   * @returns {string[]}
   */
  #uniqueValues(dimension) {
    const set = new Set();

    for (const card of this.#cards) {
      for (const value of card.values[dimension] ?? []) set.add(value);
    }

    const values = [...set];
    if (dimension === 'year') return values.sort((a, b) => Number(b) - Number(a));

    return values.sort((a, b) => a.localeCompare(b));
  }

  /**
   * Recompute card visibility and sync all dependent UI.
   */
  #applyFilters() {
    let visible = 0;

    for (const card of this.#cards) {
      const show = this.#matches(card);
      card.el.hidden = !show;
      if (show) visible += 1;
    }

    this.#updateFacetStates();
    this.#renderChips();
    this.#toggleEmptyState(visible);
    this.#toggleClearButton();
    this.#announceCount(visible);
  }

  /**
   * @param {{values: Record<string, string[]>, search: string}} card
   * @returns {boolean}
   */
  #matches(card) {
    if (this.#query && !card.search.includes(this.#query)) return false;

    for (const [dimension, set] of this.#selected) {
      if (set.size === 0) continue;
      if (!(card.values[dimension] ?? []).some((value) => set.has(value))) return false;
    }

    return true;
  }

  /**
   * Toggle the active style + count badge on each facet summary.
   */
  #updateFacetStates() {
    for (const [dimension, facet] of this.#facets) {
      const size = this.#selected.get(dimension)?.size ?? 0;
      facet.details.classList.toggle(this.#classes.facetActive, size > 0);

      if (facet.count) {
        facet.count.hidden = size === 0;
        facet.count.textContent = String(size);
      }
    }
  }

  /**
   * Render a removable chip for every selected value.
   */
  #renderChips() {
    const container = this.refs.activeChips;
    if (!container) return;

    const removeLabel = this.dataset.removeLabel ?? '';
    const fragment = document.createDocumentFragment();
    let total = 0;

    for (const [dimension, set] of this.#selected) {
      for (const value of set) {
        total += 1;

        const chip = document.createElement('span');
        chip.className = this.#classes.chip;

        const text = document.createElement('span');
        text.textContent = value;

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = this.#classes.chipRemove;
        remove.dataset.chipRemove = '';
        remove.dataset.dimension = dimension;
        remove.dataset.value = value;
        remove.setAttribute('aria-label', `${removeLabel} ${value}`.trim());
        remove.textContent = '×';

        chip.append(text, remove);
        fragment.append(chip);
      }
    }

    container.replaceChildren(fragment);
    container.hidden = total === 0;
  }

  /**
   * @param {string} dimension
   * @param {string} value
   */
  #removeValue(dimension, value) {
    const set = this.#selected.get(dimension);
    if (!set || !value) return;

    set.delete(value);

    const facet = this.#facets.get(dimension);
    const input = facet?.options.querySelector(`input[value="${CSS.escape(value)}"]`);
    if (input instanceof HTMLInputElement) input.checked = false;

    this.#applyFilters();
  }

  /**
   * @param {number} visible
   */
  #toggleEmptyState(visible) {
    if (this.refs.emptyState) this.refs.emptyState.hidden = visible !== 0;
  }

  /**
   * Show "Clear all" only when at least one filter or the search is active.
   */
  #toggleClearButton() {
    const button = this.refs.clearButton;
    if (!button) return;

    let active = this.#query.length > 0;

    if (!active) {
      for (const set of this.#selected.values()) {
        if (set.size) {
          active = true;
          break;
        }
      }
    }

    button.hidden = !active;
  }

  /**
   * @param {number} visible
   */
  #announceCount(visible) {
    const status = this.refs.status;
    if (!status) return;

    const template = status.dataset.countTemplate ?? '%count%';
    status.textContent = template.replace('%count%', String(visible));
  }

  #attachListeners() {
    this.refs.searchInput?.addEventListener('input', this.#onSearch);
    this.refs.searchInput?.addEventListener('input', this.#toggleSearchClear);
    this.refs.searchClear?.addEventListener('click', this.#onSearchClear);
    this.refs.activeChips?.addEventListener('click', this.#onChipClick);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocumentClick);

    for (const facet of this.#facets.values()) {
      facet.options.addEventListener('change', this.#onFacetChange);
    }
  }

  #removeListeners() {
    this.refs.searchInput?.removeEventListener('input', this.#onSearch);
    this.refs.searchInput?.removeEventListener('input', this.#toggleSearchClear);
    this.refs.searchClear?.removeEventListener('click', this.#onSearchClear);
    this.refs.activeChips?.removeEventListener('click', this.#onChipClick);
    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onDocumentClick);

    for (const facet of this.#facets.values()) {
      facet.options.removeEventListener('change', this.#onFacetChange);
    }
  }
}

if (!customElements.get('ax-publications-component')) {
  customElements.define('ax-publications-component', AxPublications);
}
